import React, {
  FunctionComponent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from 'data/root.reducers';
import * as globalSelectors from 'global/global-selectors';
import * as actions from 'data/contact/contact.actions';
import * as contactSelectors from 'data/contact/contact.selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { FieldUpdateHandlerParam } from 'shared/form';
import FormPanel from 'components/FormPanel';
import FormButtons from 'components/FormButtons';
import { Contact } from 'data/contact/contact.types';
import ContactForm, { ContactFormRef } from '../../components/ContactForm';
import { contactTemplate } from '../../templates/contactTemplate';
import getContactDataToSubmit from '../../utils/getContactDataToSubmit';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  deliveryFunctionId: globalSelectors.getSelectedDeliveryFunctionId(state),
  selectedContact: contactSelectors.getSelectedContact(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  contact: contactSelectors.getContact(state),
});

const mapDispatch = {
  editContact: actions.updateContact,
  goToContactList: actions.goToContactList,
  getContact: actions.getContactById,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const ContactEdit: FunctionComponent<Props> = ({
  deliveryFunctionId,
  selectedContact,
  history,
  isAuditEnabled,
  userId,
  userName,
  contact,
  editContact,
  goToContactList,
  getContact,
}: Props) => {
  const [template, setTemplate] = useState(contactTemplate);
  const [formData, setFormData] = useState<Partial<Contact>>({});
  const contactFormRef = useRef<ContactFormRef>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const id = selectedContact[0]?.id;

    if (id) {
      getContact(deliveryFunctionId, id);
    }
  }, [deliveryFunctionId, selectedContact, getContact]);

  useEffect(() => {
    if (contact != null) {
      setFormData(contact);

      const clonedTemplate = cloneDeep(contactTemplate);
      clonedTemplate.formId = Date.now().toString();

      setTemplate(clonedTemplate);
    }
  }, [contact]);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    const clonedFormData = cloneDeep(formData);

    setFormData({
      ...clonedFormData,
      [fieldName]: value,
    });
  };

  const handleClickSave = () => {
    if (contact != null && contact.id == null) {
      return;
    }

    const isValid = contactFormRef?.current?.isValid(true, true) ?? true;
    if (!isValid) {
      return;
    }

    const formValues = contactFormRef?.current?.getValues();
    if (!formValues || contact == null) {
      return;
    }

    const body = getContactDataToSubmit(
      formValues,
      contact,
      isAuditEnabled,
      userId,
      userName,
      'UPDATE',
    );

    editContact(
      deliveryFunctionId,
      contact?.id,
      body,
      history,
    );
  };

  const handleClickCancel = () => goToContactList(history);

  if (!contact) {
    return null;
  }

  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleClickCancel}
          onClickSave={handleClickSave}
          onClickClose={handleClickCancel}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="ContactEdit">
      {renderFormPanel(t('contact.edit.title'))}
      <ContactForm
        data={formData}
        template={template}
        ref={contactFormRef}
        onFieldUpdate={handleFieldUpdate}
      />
    </div>
  );
};

export default withRouter(connector(ContactEdit));
